/**
 * 爱微圈导航 · 入口：init / 内置动作 / DOMContentLoaded
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, gateGuest, isGuest, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSiteReviewModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

document.addEventListener('DOMContentLoaded', init);
export const BUILTIN_ACTIONS = {
    '天气': () => toggleWeatherPop(),
    '待办事项': () => { if (gateGuest()) return; openDrawer('#todoPanel', loadTodos); },
    '笔记': () => { if (gateGuest()) return; openDrawer('#notesPanel', loadNotes); },
    '设置': () => openSidebar('settings'),
};

/* 动画兼容检测：减少动态/低性能设备上的兼容问题 */

let suggestTimer = null;

export async function init() {
    /* 关掉浏览器的 scrollRestoration：刷新页面时浏览器会按上次滚动位置恢复，
     * 但首页本身没有纵向滚动（body overflow:hidden），恢复位置会被错误地
     * 应用到内部 .main-area 容器，导致卡片模式刷新后跳到中间段。
     * 改为手动管理：每次 init 强制滚顶，符合「刷新即回到首页顶部」的预期。 */
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    const _main = document.querySelector('.main-area');
    if (_main) _main.scrollTop = 0;

    trackVisit();
    // OAuth 回跳结果提示（api/oauth.php 登录后带参跳回）
    try {
        const q = new URLSearchParams(location.search);
        if (q.get('oauth') === 'ok') {
            const names = { qq: 'QQ', wechat: '微信', google: 'Google' };
            toast('' + (names[q.get('provider')] || '第三方') + '登录成功，数据已同步', 'success');
        } else if (q.get('oauth_error')) {
            toast(decodeURIComponent(q.get('oauth_error')), 'error');
        }
        if (q.has('oauth') || q.has('oauth_error')) {
            history.replaceState(null, '', location.pathname);
        }
    } catch (e) { /* 忽略 URL 参数异常 */ }

    // 恢复缓存：立即绘制
    let shortcutsStarted = false;
    if (restoreCache()) {
        /* 有缓存 = 老用户刷新：跳过入场动画，首屏直接静态呈现（防每次刷新整屏波浪淡入） */
        state._skipIntroAnim = true;
        /* 首屏显示模式以服务端权威值为准（index.php 注入 bootDisplay）。
         * 缓存里的 display_mode 可能已过期（用户在别处改过、或尚未同步），
         * 若按它渲染会出现「card 拉全量 / grid 取单页」的条数错配，
         * 首屏渲染出几十行后再被服务端数据替换成几行，表现为刷新后跳掉十几行。
         * 这里先用服务端值覆盖，保证首屏条数与最终数据一致。 */
        const bd = bootData.boot_display;
        if (bd && bd.display_mode) {
            state.settings.display_mode = bd.display_mode;
            if (bd.grid_cols) state.settings.grid_cols = bd.grid_cols;
            if (bd.grid_rows) state.settings.grid_rows = bd.grid_rows;
            /* 模式被纠正后，缓存里这批 shortcuts 的条数基准可能是错的：
             * 统一按当前模式裁剪，避免首屏渲染出远超一页的行数。 */
            const cap = getPageCapacity();
            if (state.settings.display_mode !== 'card' && cap > 0 && state.shortcuts.length > cap) {
                state.shortcuts = state.shortcuts.slice(0, cap);
                state.shortcutCache[state.currentPage] = state.shortcuts;
            }
        }
        applySettings();
        renderShortcuts();
        /* 缓存命中：设置已在本地合并完成，可立即并行拉取快捷方式，
         * 不必等下方 Promise.all 完成，省一趟串行往返（SWR 的 revalidate 阶段） */
        if (state.settings.display_mode !== 'card') {
            loadShortcuts();
            shortcutsStarted = true;
        }
    } else {
        state.settings = { ...DEFAULT_SETTINGS };
        /* 无缓存时同样优先采用服务端显示模式，保证首屏骨架/布局不跑偏 */
        const bd0 = bootData.boot_display;
        if (bd0 && bd0.display_mode) {
            state.settings.display_mode = bd0.display_mode;
            if (bd0.grid_cols) state.settings.grid_cols = bd0.grid_cols;
            if (bd0.grid_rows) state.settings.grid_rows = bd0.grid_rows;
        }
        applySettings();
        renderSkeleton();
    }

    tickClock();
    /* 页面不可见时暂停秒表与天气轮询，回到前台立即校准/补刷，避免后台标签页空耗 CPU 与请求 */
    let clockTimer = setInterval(tickClock, 1000);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (clockTimer) { clearInterval(clockTimer); clockTimer = null; }
        } else {
            tickClock();
            if (!clockTimer) clockTimer = setInterval(tickClock, 1000);
            if (state.settings.show_weather !== '0' && Date.now() - (state._weatherLoadedAt || 0) > 30 * 60 * 1000) loadWeather();
        }
    });

    // 批量整理工具栏事件
    initBatchToolbar();

    /* 顶部按钮 */
    if ($('#btnHome')) $('#btnHome').onclick = () => {
        modalConfirm('设为浏览器主页', '在浏览器设置中将启动页 / 新标签页设为当前网址即可。点击「确认」复制本站地址。').then(ok => {
            if (!ok) return;
            navigator.clipboard && navigator.clipboard.writeText(location.origin + '/').then(
                () => toast('地址已复制，请到浏览器设置中粘贴', 'success'),
                () => toast(location.origin + '/', 'info')
            );
        });
    };
    $('#btnTodo').onclick = () => { if (gateGuest()) return; openDrawer('#todoPanel', loadTodos); };
    $('#btnNotes').onclick = () => { if (gateGuest()) return; openDrawer('#notesPanel', loadNotes); };
    $('#btnMenu').onclick = () => openSidebar();
    if ($('#btnWallpaper')) $('#btnWallpaper').onclick = showWallpaperModal;
    $('#weatherChip').onclick = (e) => { e.stopPropagation(); toggleWeatherPop(); };
    // 风车装饰：点击打开壁纸设置
    const windmill = $('#windmill');
    if (windmill) windmill.onclick = () => openSidebar('settings');

    // 通知开关
    const notifToggle = $('#notificationToggle');
    if (notifToggle) {
        notifToggle.classList.toggle('on', state.settings.notification_enabled === '1');
        notifToggle.onclick = async () => {
            if (notifToggle.classList.contains('on')) {
                state.settings.notification_enabled = '0';
                notifToggle.classList.remove('on');
                toast('通知已关闭', 'info');
            } else {
                if ('Notification' in window) {
                    const perm = await Notification.requestPermission();
                    if (perm === 'granted') {
                        state.settings.notification_enabled = '1';
                        notifToggle.classList.add('on');
                        new Notification('通知已开启', { body: '您将收到本站的通知提醒' });
                        toast('通知已开启', 'success');
                    } else {
                        toast('请在浏览器设置中允许通知', 'error');
                        return;
                    }
                } else {
                    toast('您的浏览器不支持通知', 'error');
                    return;
                }
            }
            scheduleSave();
        };
    }

    // 动画类型选择
    $$('#animOptions .anim-option').forEach(opt => {
        opt.classList.toggle('active', opt.dataset.anim === (state.settings.animation_type || 'default'));
        opt.onclick = () => {
            $$('#animOptions .anim-option').forEach(x => x.classList.remove('active'));
            opt.classList.add('active');
            state.settings.animation_type = opt.dataset.anim;
            applySettings();
            scheduleSave();
        };
    });

    /* 抽屉内输入 */
    $('#todoInput').onkeydown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        addTodo();
    };
    $('#todoAdd').onclick = addTodo;
    $('#todoClearDone').onclick = async () => {
        const done = (state.todos || []).filter(t => Number(t.done)).length;
        if (!done) { toast('没有已完成的待办', 'info'); return; }
        const ok = await modalConfirm('清除已完成', `确认删除 ${done} 条已完成的待办？`);
        if (!ok) return;
        try {
            const res = await apiPost(API.todos + '?action=clear_done', {});
            await loadTodos();
            toast(`已清除 ${res.removed || done} 条待办`, 'success');
        } catch (e) { toast(e.message, 'error'); }
    };
    /* 待办筛选：全部 / 未完成 / 已完成（onclick 幂等，避免监听累加） */
    $$('#todoFilter .tf-item').forEach(btn => {
        btn.onclick = () => {
            $$('#todoFilter .tf-item').forEach(x => x.classList.toggle('active', x === btn));
            state.todoFilter = btn.dataset.f;
            renderTodos();
        };
    });
    /* 便签搜索：纯前端过滤，输入即时生效 */
    $('#notesSearch').oninput = (e) => {
        state.noteFilter = e.target.value || '';
        renderNotes();
    };
    $('#notesSearchClear').onclick = () => {
        $('#notesSearch').value = '';
        state.noteFilter = '';
        renderNotes();
        $('#notesSearch').focus();
    };
    $('#notesAdd').onclick = addNote;

    /* 抽屉/侧栏关闭 */
    $$('[data-close-drawer]').forEach(b => b.onclick = closeDrawers);
    const drawerOv = $('#drawerOverlay');
    if (drawerOv) drawerOv.onclick = closeDrawers;
    $('#sidebarClose').onclick = closeSidebar;
    $('#sidebarOverlay').onclick = closeSidebar;
    $$('.sidebar-tab').forEach(t => t.onclick = () => switchSidebarTab(t.dataset.tab));
    const sidebarQr = $('#btnSidebarQr');
    if (sidebarQr) sidebarQr.onclick = () => showQrSync();

    /* 搜索 */
    $$('.search-tab').forEach(t => {
        t.onclick = () => { state.searchTab = t.dataset.tab; applySearchTabUI(); };
    });
    const input = $('#searchInput');
    input.oninput = () => {
        $('#searchClear').style.display = input.value ? 'flex' : 'none';
        if (state.settings.show_search_suggest !== '1') return;
        clearTimeout(suggestTimer);
        const q = input.value.trim();
        suggestTimer = setTimeout(() => fetchSuggestions(q), 220);
    };
    input.onfocus = () => { if (input.value.trim()) fetchSuggestions(input.value.trim()); };
    input.onkeydown = (e) => { if (e.key === 'Enter') { hideSuggestions(); handleSearch(); } };
    input.onblur = () => setTimeout(hideSuggestions, 180);
    $('#searchClear').onclick = () => { input.value = ''; input.focus(); $('#searchClear').style.display = 'none'; hideSuggestions(); };
    $('#searchSubmit').onclick = () => { hideSuggestions(); handleSearch(); };
    $('#engineBtn').onclick = (e) => {
        e.stopPropagation();
        const dd = $('#engineDropdown');
        dd.classList.toggle('show');
        hideSuggestions();
    };
    document.addEventListener('click', (e) => {
        if (!e.target.closest('#engineBtn') && !e.target.closest('#engineDropdown')) $('#engineDropdown').classList.remove('show');
        if (!e.target.closest('#weatherChip') && !e.target.closest('.weather-pop')) $('#weatherPop').classList.remove('show');
        if (!e.target.closest('#contextMenu')) $('#contextMenu').classList.remove('show');
        if (!e.target.closest('.search-wrap')) hideSuggestions();
    });
    $('#modalOverlay').addEventListener('click', (e) => { if (e.target === e.currentTarget) closeModal(); });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeModal(); closeSidebar(); closeDrawers();
            $('#engineDropdown').classList.remove('show');
            $('#weatherPop').classList.remove('show');
            $('#contextMenu').classList.remove('show');
        }
        if (e.key === '/' && !e.target.matches('input,textarea,select')) {
            e.preventDefault();
            $('#searchInput').focus();
            if (e.target.value) e.target.select();
        }
        // Ctrl/Cmd + K → 聚焦搜索框（选中已有文字）
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
            e.preventDefault();
            const si = $('#searchInput');
            if (si) { si.focus(); si.select(); }
        }
        // 数字键 1-9 → 切换对应分页（非输入/模态场景）
        if (!e.target.matches('input,textarea,select') &&
            (!$('#modalOverlay') || $('#modalOverlay').style.display === 'none')) {
            const num = parseInt(e.key);
            if (num >= 1 && num <= 9 && state.pages && state.pages[num - 1]) {
                switchPage(state.pages[num - 1]);
            }
        }
    });

    // 快捷方式前端搜索过滤（节流 300ms）
    let _filterTimer = null;
    const onSearchInput = () => {
        clearTimeout(_filterTimer);
        _filterTimer = setTimeout(() => {
            state.shortcutFilterQuery = $('#searchInput').value.trim();
            // 卡片模式立即重渲，grid 模式由 renderShortcuts 触发
            if (state.settings.display_mode === 'card') {
                renderCardViewAll();
            } else {
                renderShortcuts();
            }
        }, 300);
    };
    $('#searchInput').addEventListener('input', onSearchInput);

    /* 引擎 */
    try {
        state.engines = await apiGet(API.engines + '?action=list');
    } catch (e) { state.engines = { builtin: [], custom: [] }; }

    /* 侧栏：添加面板 */
    $('#addSearchInput').oninput = debounce(() => loadWebsites(), 300);
    // 自定义添加全屏弹窗
    $('#openCustomBtn').onclick = () => showCustomAddModal();
    const pencilBtn = $('#openCustomBtn2');
    if (pencilBtn) pencilBtn.onclick = () => showCustomAddModal();
    // 分组管理
    $('#openCategoryMgrBtn').onclick = () => showCategoryMgr();
    // 推荐网站入库（提交管理员审核）
    const siteReviewBtn = $('#openSiteReviewBtn');
    if (siteReviewBtn) siteReviewBtn.onclick = () => showSiteReviewModal();

    // 拖拽链接到自定义表单快速添加
    const customForm = document.querySelector('.custom-form');
    if (customForm) {
        customForm.ondragover = (e) => {
            e.preventDefault();
            customForm.style.outline = '2px dashed var(--accent)';
            customForm.style.outlineOffset = '-2px';
            customForm.style.borderRadius = '12px';
        };
        customForm.ondragleave = (e) => {
            if (e.target === customForm || !customForm.contains(e.relatedTarget)) {
                customForm.style.outline = '';
                customForm.style.outlineOffset = '';
            }
        };
        customForm.ondrop = (e) => {
            e.preventDefault();
            customForm.style.outline = '';
            customForm.style.outlineOffset = '';
            const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain') || '';
            if (url && /^https?:\/\//i.test(url)) {
                const urlInput = $('#customUrlInput');
                if (urlInput) {
                    urlInput.value = url;
                    urlInput.dispatchEvent(new Event('input'));
                    urlInput.dispatchEvent(new Event('blur'));
                }
                toast('已填入链接，正在获取网站信息...', 'success');
            }
        };
    }

    // 书签导入
    let _parsedBookmarks = [];
    const bmDrop = $('#bookmarkDropZone');
    const bmFile = $('#bookmarkFileInput');
    const bmResult = $('#bookmarkImportResult');
    const bmImportBtn = $('#bookmarkImportBtn');
    if (bmDrop && bmFile) {
        bmDrop.onclick = () => bmFile.click();
        bmDrop.ondragover = (e) => {
            e.preventDefault();
            bmDrop.style.borderColor = 'var(--accent)';
            bmDrop.style.background = 'rgba(91,141,212,0.08)';
            e.dataTransfer.dropEffect = 'copy';
        };
        bmDrop.ondragleave = () => { bmDrop.style.borderColor = ''; bmDrop.style.background = ''; };
        bmDrop.ondrop = (e) => {
            e.preventDefault();
            bmDrop.style.borderColor = ''; bmDrop.style.background = '';
            // 优先检查拖入的文件（书签 HTML 文件）
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                parseBookmarkFile(e.dataTransfer.files[0]);
                return;
            }
            // 拖入的是链接/书签 URL
            const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/x-moz-url') || e.dataTransfer.getData('text/plain') || '';
            const title = e.dataTransfer.getData('text/x-moz-url-title') || '';
            if (url && /^https?:\/\//i.test(url)) {
                // 直接添加这个链接为快捷方式
                toast('正在获取网站信息并添加…', 'success');
                (async () => {
                    let name = title || '';
                    let iconColor = '#5B8DD4';
                    let iconText = '';
                    let iconUrl = '';
                    // 尝试获取网站信息
                    try {
                        const info = await apiGet(API.site_info + '?url=' + encodeURIComponent(url));
                        if (info) {
                            if (!name && info.title) name = info.title.substring(0, 50);
                            if (info.icon) iconUrl = info.icon;
                            if (info.icon_text) iconText = info.icon_text;
                            if (info.icon_color) iconColor = info.icon_color;
                        }
                    } catch (e) {}
                    if (!name) name = url.replace(/^https?:\/\//, '').split('/')[0];
                    if (!iconText) iconText = name.charAt(0).toUpperCase();
                    try {
                        await apiPost(API.shortcuts + '?action=create', {
                            name: name.substring(0, 50),
                            url: url,
                            icon_text: iconText,
                            icon_color: iconColor,
                            icon_url: iconUrl,
                            page: state.currentPage,
                        });
                        await loadShortcuts();
                        toast('已添加：' + name, 'success');
                    } catch (err) {
                        toast('添加失败：' + err.message, 'error');
                    }
                })();
            } else {
                toast('请拖入书签链接或书签 HTML 文件', 'info');
            }
        };
        bmFile.onchange = (e) => { if (e.target.files[0]) parseBookmarkFile(e.target.files[0]); };
    }
    function parseBookmarkFile(file) {
        if (!file.type.includes('html') && !file.name.endsWith('.html')) {
            toast('请选择书签 HTML 文件', 'error'); return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const html = e.target.result;
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const links = doc.querySelectorAll('a[href]');
            const bookmarks = [];
            const seen = new Set();
            links.forEach(a => {
                const url = a.getAttribute('href') || '';
                if (!url || seen.has(url)) return;
                if (url.startsWith('javascript:') || url.startsWith('place:')) return;
                if (!/^https?:/i.test(url)) return;
                seen.add(url);
                const name = (a.textContent || '').trim() || url;
                const addDate = a.getAttribute('add_date');
                bookmarks.push({ name, url, addDate });
            });
            _parsedBookmarks = bookmarks.slice(0, 200); // 最多导入200个
            if (_parsedBookmarks.length === 0) {
                toast('未找到有效的书签', 'error'); return;
            }
            bmResult.style.display = 'block';
            bmResult.innerHTML = `
                <div style="color:#1f2430;font-weight:600;margin-bottom:8px">
                    解析到 <strong style="color:var(--accent)">${_parsedBookmarks.length}</strong> 个书签
                </div>
                <div style="max-height:160px;overflow-y:auto;border:1px solid #e5e7eb;border-radius:8px;padding:6px 8px;background:#f9fafb">
                    ${_parsedBookmarks.slice(0, 10).map(b => `
                        <div style="padding:4px 6px;font-size:12px;color:#4b5563;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                            ${esc(b.name)} <span style="color:#9ca3af">· ${b.url.replace(/^https?:\/\//, '').split('/')[0]}</span>
                        </div>
                    `).join('')}
                    ${_parsedBookmarks.length > 10 ? `<div style="padding:4px 6px;font-size:11.5px;color:#9ca3af;text-align:center">还有 ${_parsedBookmarks.length - 10} 个...</div>` : ''}
                </div>
            `;
            bmImportBtn.style.display = 'block';
        };
        reader.readAsText(file);
    }
    if (bmImportBtn) {
        bmImportBtn.onclick = async () => {
            if (_parsedBookmarks.length === 0) return;
            bmImportBtn.disabled = true;
            bmImportBtn.textContent = '导入中...';
            let success = 0, fail = 0;
            for (const bm of _parsedBookmarks) {
                try {
                    await apiPost(API.shortcuts + '?action=create', {
                        name: bm.name.substring(0, 50),
                        url: bm.url,
                        icon_text: bm.name.charAt(0).toUpperCase(),
                        icon_color: randomColor(),
                        /* 批量导入也尽量拿到真实图标：走 favicon 代理（无调用限额、服务端缓存 7 天），
                         * 不用 site_info（它有 10 分钟 20 次的限流，200 条书签必然打满）。
                         * 抓不到时 renderIcon 会自动回落到上面的「文字 + 随机色」。 */
                        icon_url: faviconOf(bm.url),
                        page: state.currentPage,
                    });
                    success++;
                } catch (e) { fail++; }
            }
            bmImportBtn.disabled = false;
            bmImportBtn.textContent = '确认导入';
            loadShortcuts();
            toast(`导入完成：成功 ${success} 个${fail ? `，失败 ${fail} 个` : ''}`, success ? 'success' : 'error');
            if (success) {
                _parsedBookmarks = [];
                bmResult.style.display = 'none';
                bmImportBtn.style.display = 'none';
                bmFile.value = '';
            }
        };
    }
    const bmHelp = $('#bookmarkHelpLink');
    if (bmHelp) {
        bmHelp.onclick = () => {
            toast('Chrome: 右上角⋯→书签→书签管理器→⋯→导出书签；Edge: 同理；Firefox: 书签→管理书签→导入和备份→导出', 'info', 6000);
        };
    }

    $('#createFolderBtn').onclick = () => {
        openModal(`
            <div class="modal-title">新建文件夹</div>
            <div class="modal-error" id="folderCreateErr"></div>
            <div class="form-group"><label>文件夹名称</label><input type="text" class="form-input" id="folderName" placeholder="文件夹" maxlength="50"></div>
            <div class="form-row2">
                <div class="form-group"><label>图标文字</label><input type="text" class="form-input" id="folderIconText" placeholder="📁" maxlength="2"></div>
                <div class="form-group"><label>图标颜色</label><input type="color" class="form-input" id="folderColor" value="#5B8DD4"></div>
            </div>
            <div class="modal-actions">
                <button class="btn btn-light" data-close-modal>取消</button>
                <button class="btn btn-primary" id="folderCreateSave">创建</button>
            </div>`);
        $('#folderCreateSave').onclick = async () => {
            try {
                await apiPost(API.shortcuts + '?action=create_folder', {
                    name: $('#folderName').value.trim() || '文件夹',
                    icon_text: $('#folderIconText').value.trim() || '📁',
                    icon_color: $('#folderColor').value,
                    page: state.currentPage,
                });
                closeModal();
                closeSidebar();
                await loadShortcuts();
                toast('文件夹已创建', 'success');
            } catch (e) { showModalError('#folderCreateErr', e.message); }
        };
    };

    /* 我的面板 */
    $('#profileCard').onclick = () => { if (!state.user || state.user.is_guest) showAuthModal('login'); else showAccountModal(); };
    $('#btnAccount').onclick = (e) => {
        e.stopPropagation();
        if (state.user && state.user.is_guest) showAuthModal('login');
        else if (state.user) showAccountModal();
        else showAuthModal('login');
    };
    $('#btnLogout').onclick = async () => {
        const ok = await modalConfirm('退出登录', '确认退出当前账号？');
        if (!ok) return;
        await doLogout();
    };
    setupDataButtons();

    /* 翻页 */
    $('#pagePrev').onclick = goPrevPage;
    $('#pageNext').onclick = goNextPage;
    $('#pagePrev').onmouseenter = () => {
        const idx = state.pages.indexOf(state.currentPage);
        if (idx > 0) prefetchPage(state.pages[idx - 1]);
    };
    $('#pageNext').onmouseenter = () => {
        const idx = state.pages.indexOf(state.currentPage);
        if (idx >= 0 && idx < state.pages.length - 1) prefetchPage(state.pages[idx + 1]);
    };

    /* 键盘左右箭头翻页：仅在非输入/模态场景下生效，更顺手 */
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
        if ($('#modalOverlay') && $('#modalOverlay').style.display !== 'none') return;
        const idx = state.pages.indexOf(state.currentPage);
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            if (idx > 0) goPrevPage();
        } else {
            e.preventDefault();
            goNextPage();
        }
    });

    /* 异步加载数据（P1-4：settings / 分类 / 待办 并行拉取，互不阻塞） */
    await Promise.all([
        loadSettings(),
        loadCategories().catch(() => {}),
        // 游客的待办接口已锁登录，跳过避免 403 噪音（抽屉入口已拦截）
        isGuest() ? Promise.resolve() : loadTodos(),
    ]);
    bindSettingsUI();
    renderProfile();
    // quickadd 收藏页等外部入口跳回：?login=1 且当前游客 → 自动弹登录框（弹完清参数防刷新复弹）
    try {
        const qp = new URLSearchParams(location.search);
        if (qp.get('login') === '1' && isGuest()) {
            showAuthModal('login');
            history.replaceState(null, '', location.pathname);
        }
    } catch (e) {}
    // 时钟下方每日内容：如果设置了非问候语模式，页面加载时拉取
    const initDm = state.settings.daily_greet_mode || 'greeting';
    if (initDm !== 'greeting') loadDailyContent(initDm);
    // 主动拉取快捷方式：缓存仅用于首屏即时绘制，必须以服务器数据为准。
    // 否则缓存过期/缺失（如登录后换设备、超过 5 分钟 TTL）时网格为空，
    // 只有切换到详细模式（卡片模式 renderCardViewAll 会自行拉取）再切回才显示。
    if (!shortcutsStarted && state.settings.display_mode !== 'card') {
        loadShortcuts();
    }

    /* 刷新防跳中间段（第二重保险）：
     * head 内联脚本已设置 scrollRestoration='manual' + DOMContentLoaded/pageshow 时复位，
     * 此处再补一次：async init 完成后若异步加载（如 settings/shortcuts）触发了滚动恢复，
     * 仍强制拉回顶部。同步 + 异步两路兜底，覆盖 Safari/部分浏览器「HTML 解析期恢复滚动」
     * 与「异步渲染完成后再恢复滚动」两种时机。 */
    const _finalReset = () => {
        try {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            const m = document.querySelector('.main-area');
            if (m) m.scrollTop = 0;
        } catch (e) {}
    };
    requestAnimationFrame(_finalReset);
    setTimeout(_finalReset, 0);
    setTimeout(_finalReset, 60);

    /* 交还滚动控制权：head 内联脚本的 rAF「钉顶」循环检测到 window.__navReady
     * 后会立即停止，把滚动权交回用户。放在 _finalReset 三连兜底之后，
     * 确保解锁前最后一刻位置仍是顶部。 */
    window.__navReady = true;

    /* 天气每 30 分钟刷新（页面不可见时跳过，恢复前台时由 visibilitychange 补刷） */
    setInterval(() => { if (!document.hidden) loadWeather(); }, 30 * 60 * 1000);
    /* 加载用户分组列表 */
    loadUserCategories();

    /* 窗口尺寸变化时重排网格 */
    window.addEventListener('resize', debounce(() => renderShortcuts(), 200));

    /* 快速添加（bookmarklet） */
    const params = new URLSearchParams(location.search);
    if (params.get('quickadd') === '1') {
        const url = decodeURIComponent(params.get('url') || '');
        const title = decodeURIComponent(params.get('title') || '');
        if (url) {
            setTimeout(() => showQuickAddModal(url, title), 500);
        }
    }

    /* 一键收藏到导航 · bookmarklet 按钮 + 复制源码 */
    (function setupBookmarklet(){
        const btn   = $('#bookmarkletBtn');
        const src   = $('#quickaddSource');
        const copy  = $('#quickaddCopy');
        if (!btn || !src) return;

        // 动态拼 quickadd 入口；始终使用当前页的 scheme/host/path，避免 PHP 写死
        const here  = window.location;
        // 取 index.php 的目录路径，比如 /ceshi/
        const parts = here.pathname.replace(/\/[^\/]*$/, '').split('/').filter(Boolean);
        const dir = '/' + parts.join('/') + '/';
        const quickaddUrl = here.protocol + '//' + here.host + dir + 'quickadd.php';

        // bookmarklet 源码：在用户当前浏览的网页上点击书签栏里的 "收藏到导航" 时执行。
        // 抓 url/title/favicon/description，组合 query string 然后在新窗口打开 quickadd.php。
        // 写法要点：尽量短小、URI 安全（避开撇号/双引号），兼容 Chrome/Edge/Firefox/Safari。
        const bm = [
            "javascript:(function(){",
            "var%20u=encodeURIComponent(location.href);",
            "var%20t=encodeURIComponent(document.title||'');",
            "var%20d=encodeURIComponent((document.querySelector('meta[name=\"description\"]')||{}).content||(document.querySelector('meta[property=\"og:description\"]')||{}).content||'');",
            "var%20i='';",
            "try{var%20ls=document.getElementsByTagName('link');for(var%20k=0;k<ls.length;k++){var%20r=(ls[k].rel||'').toLowerCase();if(r.indexOf('icon')>=0){i=ls[k].href;break;}}}catch(e){}",
            "if(!i){var%20re=/^https?:\\/\\/[^/]+/;i=location.origin+'/favicon.ico';}",
            "var%20w=window.open('" + quickaddUrl.replace(/'/g, "%27") + "?quickadd=1&url='+u+'&title='+t+'&desc='+d+'&icon='+encodeURIComponent(i),",
            "'nav-bookmarklet','width=620,height=760,left='+Math.max(0,(screen.width-620)/2)+',top='+Math.max(0,(screen.height-760)/2));",
            "if(!w){alert('请允许弹窗以使用一键收藏');}",
            "})();"
        ].join('');

        btn.href = bm;
        // 让拖到书签栏时的默认标题更友好（部分浏览器会读 title 属性作为书签默认名）
        btn.setAttribute('title', '收藏到 ' + (document.title || '我的导航'));
        btn.setAttribute('aria-label', '收藏到导航');

        // 备用：可复制的源码（同样的 javascript: 表达式）
        src.value = bm;

        // 直接点击的兜底：在本站内点击时，让用户跳到 quickadd.php 让其手动粘贴 URL。
        // （书签栏里的书签点击时执行的是 javascript: 表达式，不会走这个分支）
        btn.addEventListener('click', function(e){
            if (here.pathname.endsWith('/quickadd.php')) return; // quickadd 内部 self
            // 当 href 不是 javascript: 而是 # 时（极少数情况）→ 防止 a 标签默认行为
            if (!/^javascript:/i.test(btn.href)) {
                e.preventDefault();
                window.open(quickaddUrl, 'nav-bookmarklet', 'width=620,height=820');
            }
        });

        if (copy) {
            copy.addEventListener('click', async function(){
                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(src.value);
                    } else {
                        src.removeAttribute('readonly');
                        src.select(); src.setSelectionRange(0, 99999);
                        document.execCommand('copy');
                        src.setAttribute('readonly', '');
                    }
                    toast('已复制到剪贴板，去浏览器添加书签时把 URL 粘贴进去即可', 'success', 4000);
                } catch (err) {
                    toast('复制失败，请手动复制下方代码', 'error');
                }
            });
        }
    })();

    /* 初始化多语言 */
    if (window.i18n) window.i18n.init();

    /* 首次访问引导 */
    maybeShowOnboarding();
}


